import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ContentApprovalList from "../Components/ContentApprovalList";
import AdminChaptersModeration from "../Components/AdminChaptersModeration";
import ContentManagementPanel from "../Components/ContentManagementPanel";
import { usePermissionCheck } from "../hooks/usePermissionCheck";
import { useAdminSession } from "../context/AdminSessionContext";
import { hasPermission } from "../utils/permissionHelpers";
import { hasRoleAtLeast } from "../utils/roleHelpers";
import { getPendingContentSubmissions, type PendingContentSubmissionPage } from "../api/adminContentApi";
import { getAdminDashboardQueueStats, type QueueStats } from "../api/adminStatsApi";

export default function AdminContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { loading, allowed } = usePermissionCheck("Content.View");
  const { session } = useAdminSession();

  const [tab, setTab] = useState<TabKey>("manage");
  const [pendingContentCount, setPendingContentCount] = useState(0);
  const [pendingChapterCount, setPendingChapterCount] = useState(0);

  const creatorRestricted = useMemo(() => {
    if (!session?.roles?.length) {
      return false;
    }
    return hasRoleAtLeast(session.roles, "creator") && !hasRoleAtLeast(session.roles, "moderator");
  }, [session]);

  const canManageContent = useMemo(() => (session ? hasPermission(session.permissions, "Content.Create") : false), [session]);
  const canModerateContent = useMemo(() => (session ? hasPermission(session.permissions, "Content.Publish") : false), [session]);
  const effectiveManage = creatorRestricted || canManageContent;
  const effectiveModerate = !creatorRestricted && canModerateContent;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedTab = params.get("tab") as TabKey | null;

    if (requestedTab && isTabAllowed(requestedTab, effectiveManage, effectiveModerate)) {
      setTab(requestedTab);
    } else if (!isTabAllowed(tab, effectiveManage, effectiveModerate)) {
      setTab("manage");
    }
  }, [location.search, effectiveManage, effectiveModerate, tab]);

  useEffect(() => {
    if (!allowed || !effectiveModerate) {
      setPendingContentCount(0);
      setPendingChapterCount(0);
      return;
    }

    const loadCounts = async () => {
      try {
        const [contentPage, chapterStats] = await Promise.all([
          getPendingContentSubmissions(0, 1),
          getAdminDashboardQueueStats("24h"),
        ]);

        setPendingContentCount((contentPage as PendingContentSubmissionPage).totalCount ?? 0);
        setPendingChapterCount((chapterStats as QueueStats | null)?.pendingChaptersCount ?? 0);
      } catch {
        setPendingContentCount(0);
        setPendingChapterCount(0);
      }
    };

    void loadCounts();
  }, [allowed, effectiveModerate]);

  if (loading) {
    return <div className="workspace-panel rounded-2xl p-8 text-center text-sm text-slate-600">Yetkiler kontrol ediliyor...</div>;
  }

  if (!allowed) {
    return <div className="admin-soft-alert text-sm">Bu alana erisim izniniz yok.</div>;
  }

  const handleTabChange = (next: TabKey) => {
    if (!isTabAllowed(next, effectiveManage, effectiveModerate)) {
      return;
    }

    setTab(next);
    const params = new URLSearchParams(location.search);
    params.set("tab", next);
    navigate({ pathname: "/admin/content-360", search: `?${params.toString()}` }, { replace: true });
  };

  return (
    <div className="space-y-4">
      <section className="workspace-panel rounded-2xl p-4">
        <h2 className="text-lg font-bold text-slate-900">Icerik Operasyonlari</h2>
        <p className="text-sm text-slate-600">Seri yonetimi, moderasyon kuyrugu ve bolum onay akislarini tek merkezden yonet.</p>
      </section>

      {effectiveModerate && (
        <section className="grid gap-3 md:grid-cols-2">
          <AlertCard
            tone={pendingContentCount > 0 ? "warning" : "success"}
            title="Uye icerik gonderimleri"
            body={
              pendingContentCount > 0
                ? `${pendingContentCount} icerik onay bekliyor. Onayla sekmesine gecerek islemleri tamamla.`
                : "Onay bekleyen icerik bulunmuyor."
            }
            actionHref={pendingContentCount > 0 ? "/admin/content-360?tab=content" : undefined}
            actionLabel={pendingContentCount > 0 ? "Onay kuyrugu" : undefined}
          />

          <AlertCard
            tone={pendingChapterCount > 0 ? "warning" : "success"}
            title="Bolum onay kuyrugu"
            body={
              pendingChapterCount > 0
                ? `${pendingChapterCount} bolum onay bekliyor. Bolum Onayi sekmesini kontrol et.`
                : "Onay bekleyen bolum bulunmuyor."
            }
            actionHref={pendingChapterCount > 0 ? "/admin/content-360?tab=content" : undefined}
            actionLabel={pendingChapterCount > 0 ? "Bolum onayi" : undefined}
          />
        </section>
      )}

      <section className="workspace-panel rounded-2xl p-3">
        <div className="flex flex-wrap gap-2">
          {effectiveManage && (
            <TabButton active={tab === "manage"} onClick={() => handleTabChange("manage")}>
              Icerik Yonetimi
            </TabButton>
          )}
          <TabButton active={tab === "approve"} onClick={() => handleTabChange("approve")} disabled={!effectiveModerate}>
            Seri Onay
          </TabButton>
          {effectiveModerate && (
            <TabButton active={tab === "parts"} onClick={() => handleTabChange("parts")} disabled={!effectiveModerate}>
              Bolum Onayi
            </TabButton>
          )}
        </div>
      </section>

      {tab === "manage" && effectiveManage && <ContentManagementPanel />}
      {tab === "approve" && effectiveModerate && <ContentApprovalList />}
      {tab === "parts" && effectiveModerate && <AdminChaptersModeration />}
    </div>
  );
}

type TabKey = "manage" | "approve" | "parts";

function TabButton({
  active,
  onClick,
  children,
  disabled,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
        active
          ? "border-sky-500 bg-sky-600 text-white shadow-[0_10px_18px_rgba(14,113,255,0.28)]"
          : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
      } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
    >
      {children}
    </button>
  );
}

function isTabAllowed(tab: TabKey, canManage?: boolean, canModerate?: boolean): boolean {
  if (tab === "manage") {
    return Boolean(canManage);
  }
  if (tab === "approve" || tab === "parts") {
    return Boolean(canModerate);
  }
  return false;
}

function AlertCard({
  tone,
  title,
  body,
  actionHref,
  actionLabel,
}: {
  tone: "warning" | "success";
  title: string;
  body: string;
  actionHref?: string;
  actionLabel?: string;
}) {
  const styles =
    tone === "warning"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : "border-emerald-200 bg-emerald-50 text-emerald-800";

  return (
    <article className={`rounded-2xl border p-3 text-sm ${styles}`}>
      <div className="font-semibold">{title}</div>
      <div className="mt-1 text-xs">{body}</div>
      {actionHref && actionLabel && (
        <a href={actionHref} className="mt-2 inline-flex rounded-lg border px-3 py-1 text-xs font-semibold hover:underline">
          {actionLabel}
        </a>
      )}
    </article>
  );
}
